// Session hooks — the WRAPPER layer where TanStack Query is used directly (feature code may not,
// per the no-raw-useQuery lint rule). These fetch the person bootstrap + the server navigation document that
// the shell renders from (capabilities-as-data).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { fetchMe, fetchNavigation } from "../auth/session.js";
import { pushToast } from "../toasts.js";
import { ApiError, errorMessage } from "./apiError.js";

type MeDocument = components["schemas"]["MeDocument"];

/** `GET /me` — person + seats + active context + global capability floor. Not cached pre-auth server-side.*/
export function useMe(options: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["me"],
    queryFn: () => fetchMe(api),
    staleTime: 60_000,
    // A page that also renders signed out (e.g. /reset) passes `enabled: signedIn` so it never asks for /me
    // without a session.
    enabled: options.enabled ?? true,
  });
}

/**
 * `GET /me/navigation?context=band:{id}` — the server-driven nav/capability document. The shell's
 * top-nav renders from `surfaces`; BandContext is built from it. `context` is `band:{id}` or `org:{id}`.
 */
export function useNavigation(context: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["me", "navigation", context ?? "none"],
    queryFn: () => fetchNavigation(api, context),
    enabled: context !== undefined,
    staleTime: 60_000,
  });
}

/**
 * `PUT /me/starred-band` — star the band this person opens the app on, or clear it with `null` (ART-319).
 * OPTIMISTIC on the `/me` document (the star flips the moment it is pressed), rolled back + toasted on error.
 * Only `["me"]` itself is touched — exact, so the navigation documents under that prefix are not refetched.
 */
export function useSetStarredBand() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation<void, Error, string | null, { previous: MeDocument | undefined }>({
    mutationFn: async (bandId) => {
      const { error, response } = await api.PUT("/api/v1/me/starred-band", { body: { bandId } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onMutate: async (bandId) => {
      await qc.cancelQueries({ queryKey: ["me"], exact: true });
      const previous = qc.getQueryData<MeDocument>(["me"]);
      if (previous) qc.setQueryData<MeDocument>(["me"], { ...previous, starredBandId: bandId });
      return { previous };
    },
    onError: (err, _bandId, ctx) => {
      if (ctx?.previous) qc.setQueryData(["me"], ctx.previous);
      pushToast(errorMessage(err, "Couldn't update your starred band"));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["me"], exact: true }),
  });
}
